import React, { useState, useEffect, useCallback } from 'react';
import {
  Sparkles,
  Heart,
  TrendingUp,
  AlertTriangle,
  Lightbulb,
  Filter,
  Calendar,
  Loader2,
  ChevronDown,
  Search,
  X
} from 'lucide-react';
import { Button, Card } from '../ui';
import { InsightCard } from './InsightCard';
import { WeeklySummaryView } from './WeeklySummaryView';
import { morningPagesService, type MorningPageInsightDto, type InsightType } from '../../services';
import './InsightsDashboard.css';

type TabType = 'insights' | 'weekly';
type FilterType = 'all' | InsightType;

interface InsightsDashboardProps {
  defaultTab?: TabType;
}

const insightTypeFilters: { value: FilterType; label: string; icon: React.FC<{ size?: number }> }[] = [
  { value: 'all', label: 'All Insights', icon: Sparkles },
  { value: 'Theme', label: 'Themes', icon: Sparkles },
  { value: 'Emotion', label: 'Emotions', icon: Heart },
  { value: 'Pattern', label: 'Patterns', icon: TrendingUp },
  { value: 'SelfSabotage', label: 'Self-Sabotage', icon: AlertTriangle },
  { value: 'Recommendation', label: 'Recommendations', icon: Lightbulb },
];

export const InsightsDashboard: React.FC<InsightsDashboardProps> = ({
  defaultTab = 'insights',
}) => {
  const [activeTab, setActiveTab] = useState<TabType>(defaultTab);
  const [insights, setInsights] = useState<MorningPageInsightDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [selectedType, setSelectedType] = useState<FilterType>('all');
  const [actionableOnly, setActionableOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const loadInsights = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await morningPagesService.getAllInsights({
        insightType: selectedType === 'all' ? undefined : selectedType,
        actionableOnly,
      });
      setInsights(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load insights');
    } finally {
      setIsLoading(false);
    }
  }, [selectedType, actionableOnly]);

  useEffect(() => {
    if (activeTab === 'insights') {
      loadInsights();
    }
  }, [activeTab, loadInsights]);

  // Filter insights by search query
  const filteredInsights = insights.filter((insight) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      insight.title.toLowerCase().includes(query) ||
      insight.content.toLowerCase().includes(query)
    );
  });

  // Group insights by date
  const groupedInsights = filteredInsights.reduce((groups, insight) => {
    const date = new Date(insight.createdAt).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(insight);
    return groups;
  }, {} as Record<string, MorningPageInsightDto[]>);

  const insightStats = {
    total: insights.length,
    actionable: insights.filter((i) => i.isActionable).length,
    themes: insights.filter((i) => i.insightType === 'Theme').length,
    patterns: insights.filter((i) => i.insightType === 'Pattern').length,
  };

  return (
    <div className="insights-dashboard">
      {/* Tabs */}
      <div className="insights-dashboard__tabs">
        <button
          className={`insights-dashboard__tab ${activeTab === 'insights' ? 'insights-dashboard__tab--active' : ''}`}
          onClick={() => setActiveTab('insights')}
        >
          <Sparkles size={18} />
          Insights
        </button>
        <button
          className={`insights-dashboard__tab ${activeTab === 'weekly' ? 'insights-dashboard__tab--active' : ''}`}
          onClick={() => setActiveTab('weekly')}
        >
          <Calendar size={18} />
          Weekly Summary
        </button>
      </div>

      {activeTab === 'insights' ? (
        <>
          {/* Stats Bar */}
          <div className="insights-dashboard__stats">
            <div className="insights-dashboard__stat">
              <span className="insights-dashboard__stat-value">{insightStats.total}</span>
              <span className="insights-dashboard__stat-label">Total Insights</span>
            </div>
            <div className="insights-dashboard__stat">
              <span className="insights-dashboard__stat-value">{insightStats.actionable}</span>
              <span className="insights-dashboard__stat-label">Actionable</span>
            </div>
            <div className="insights-dashboard__stat">
              <span className="insights-dashboard__stat-value">{insightStats.themes}</span>
              <span className="insights-dashboard__stat-label">Themes</span>
            </div>
            <div className="insights-dashboard__stat">
              <span className="insights-dashboard__stat-value">{insightStats.patterns}</span>
              <span className="insights-dashboard__stat-label">Patterns</span>
            </div>
          </div>

          {/* Filters */}
          <div className="insights-dashboard__filters">
            <div className="insights-dashboard__search">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search insights..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} aria-label="Clear search">
                  <X size={16} />
                </button>
              )}
            </div>

            <Button
              variant={showFilters ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={16} />
              Filters
              <ChevronDown
                size={16}
                className={`insights-dashboard__filter-chevron ${showFilters ? 'insights-dashboard__filter-chevron--open' : ''}`}
              />
            </Button>
          </div>

          {showFilters && (
            <Card variant="outlined" padding="md" className="insights-dashboard__filter-panel">
              <div className="insights-dashboard__filter-group">
                <label className="insights-dashboard__filter-label">Type</label>
                <div className="insights-dashboard__filter-options">
                  {insightTypeFilters.map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      className={`insights-dashboard__filter-btn ${selectedType === value ? 'insights-dashboard__filter-btn--active' : ''}`}
                      onClick={() => setSelectedType(value)}
                    >
                      <Icon size={14} />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="insights-dashboard__filter-group">
                <label className="insights-dashboard__filter-checkbox">
                  <input
                    type="checkbox"
                    checked={actionableOnly}
                    onChange={(e) => setActionableOnly(e.target.checked)}
                  />
                  <span>Show actionable insights only</span>
                </label>
              </div>
            </Card>
          )}

          {/* Content */}
          {isLoading ? (
            <div className="insights-dashboard__loading">
              <Loader2 size={32} className="insights-dashboard__spinner" />
              <p>Loading insights...</p>
            </div>
          ) : error ? (
            <div className="insights-dashboard__error">
              <p>{error}</p>
              <Button onClick={loadInsights}>Try Again</Button>
            </div>
          ) : filteredInsights.length === 0 ? (
            <div className="insights-dashboard__empty">
              <div className="insights-dashboard__empty-icon">
                <Sparkles size={48} />
              </div>
              <h3>No insights yet</h3>
              <p>
                {searchQuery
                  ? 'No insights match your search. Try a different query.'
                  : 'Upload morning pages and analyze them to discover personalized insights.'}
              </p>
            </div>
          ) : (
            <div className="insights-dashboard__content">
              {Object.entries(groupedInsights).map(([date, dateInsights]) => (
                <div key={date} className="insights-dashboard__group">
                  <h3 className="insights-dashboard__group-title">
                    <Calendar size={16} />
                    {date}
                  </h3>
                  <div className="insights-dashboard__grid">
                    {dateInsights.map((insight) => (
                      <InsightCard
                        key={insight.id}
                        insight={insight}
                        showDate={false}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <WeeklySummaryView />
      )}
    </div>
  );
};

export default InsightsDashboard;
